import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { trpc, trpcClient } from '@/lib/trpc'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useDocumentTitle } from 'usehooks-ts'
import { OnboardingWizard } from '@/components/screenata/onboarding/OnboardingWizard'
import { OnboardingSkeleton } from '@/components/screenata/onboarding/OnboardingSkeleton'
import { APP_NAME } from '@/lib/env'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { stageToWizardStep } from '@/lib/screenata-phase'

/**
 * The program-context wizard. RFC-231 replaced it with the Setup guide, but a
 * 20-question form is not something to fill in a checklist or a chat thread —
 * so both exist: the guide is the map, and its `company.profile` step
 * deep-links here via `?step=<key>`. The legacy OnboardingChat fallback is
 * gone (RFC-231 deleted it); the guide's chapter 0 owns setup progress.
 */
export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/onboarding',
)({
  // `?step=<key>` deep-links a wizard screen by its key (e.g.
  // `?step=infrastructureModel`). Key, not index, so it survives
  // skipWhen-filtered screens and screen reordering.
  validateSearch: (search: Record<string, unknown>): { step?: string } => ({
    step: typeof search.step === 'string' ? search.step : undefined,
  }),
  component: ScreenataOnboardingPage,
})

function ScreenataOnboardingPage() {
  useDocumentTitle(`Get Started - ${APP_NAME}`)

  const { workspaceId } = useParams({ from: '/(protected)/workspaces/$workspaceId/onboarding' })
  const { isAdmin, isLoading: isLoadingPermissions } =
    useWorkspacePermissions(workspaceId)
  const { step: stepKey } = Route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [programId, setProgramId] = useState<string | null>(null)
  const [initError, setInitError] = useState<string | null>(null)

  // RFC-213 §6: with no explicit `?step=`, resume where the work actually got
  // to rather than at screen one. The stage is derived server-side from the
  // same function `/v1/context` gives the coding agent, so progress an agent
  // made while this tab was closed is picked up here.
  const stageQuery = useQuery({
    ...trpc.complianceProgram.getOnboardingStage.queryOptions(),
    enabled: !isLoadingPermissions && isAdmin && !stepKey,
  })
  const resumeStepKey =
    stepKey ??
    stageToWizardStep(stageQuery.data?.stage, stageQuery.data?.completed)

  // Reset state when workspace changes
  useEffect(() => {
    setProgramId(null)
    setInitError(null)
  }, [workspaceId])

  // Initialize or get compliance program
  const { mutateAsync: initProgram } = useMutation(
    trpc.complianceProgram.initProgram.mutationOptions()
  )
  const programQueryOptions = useMemo(() => ({
    ...trpc.complianceProgram.getProgram.queryOptions(),
    queryKey: [
      ...trpc.complianceProgram.getProgram.queryKey(),
      workspaceId,
    ],
    enabled: !isLoadingPermissions && isAdmin,
  }), [isAdmin, isLoadingPermissions, workspaceId])
  const programQuery = useQuery(programQueryOptions)

  useEffect(() => {
    if (programQuery.data?.id) {
      setProgramId(programQuery.data.id)
    }
  }, [programQuery.data])
  const handleInitProgram = useCallback(async () => {
    if (!isAdmin) return
    if (!programId) {
      try {
        const result = await initProgram({})
        setProgramId(result.id)
        queryClient.invalidateQueries({ queryKey: programQueryOptions.queryKey })
      } catch (err) {
        console.error('Failed to init compliance program:', err)
        setInitError(String(err))
      }
    }
  }, [isAdmin, initProgram, programId, programQueryOptions.queryKey, queryClient])

  // Auto-init program (no welcome screen to trigger it)
  useEffect(() => {
    if (!isAdmin) return
    if (programQuery.isLoading) return
    if (programId) return
    if (programQuery.data?.id) return
    void handleInitProgram()
  }, [isAdmin, programQuery.isLoading, programId, programQuery.data?.id, handleInitProgram])

  // Called by OnboardingChat when the agent calls completeOnboarding.
  // Marks the step done server-side and refreshes the program so
  // the "Continue" button below becomes visible.
  const handleComplete = useCallback(async () => {
    if (!isAdmin) return
    if (programId) {
      try {
        await trpcClient.complianceProgram.completeOnboardingStep.mutate({
          programId,
          step: 'get-started',
        })
      } catch {
        // Best-effort — the chat tool may have already marked it complete
      }
    }
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getProgram.queryKey(),
    })
    // No navigation here: the wizard shows its setup-progress step next and
    // that step owns the exit into the Setup guide. Leaving from here would
    // skip it.
  }, [isAdmin, queryClient, programId])

  if (isLoadingPermissions) {
    return <OnboardingSkeleton />
  }

  if (!isAdmin) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-text-subtle">
        <p className="font-medium text-text-secondary">Onboarding requires workspace admin access.</p>
        <p>Ask a workspace admin to configure this workspace.</p>
      </div>
    )
  }

  if (initError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-text-subtle">
        <p>Failed to initialize compliance program.</p>
        <button
          onClick={() => { setInitError(null); void handleInitProgram() }}
          className="rounded-sm border border-ring px-4 py-1.5 text-cobalt-strong hover:bg-selected"
        >
          Retry
        </button>
      </div>
    )
  }

  if (!programId ) {
    return <OnboardingSkeleton />
  }

  // `initialStepKey` is read once on mount, so resolve the resume target
  // before the wizard exists — otherwise it opens on screen one and the
  // derived stage arrives too late to be honoured.
  if (!stepKey && stageQuery.isLoading) {
    return <OnboardingSkeleton />
  }

  return (
    <OnboardingWizard
      programId={programId}
      onComplete={handleComplete}
      initialStepKey={resumeStepKey}
      // Once onboarding is finished this sync would put `?step=` back and undo
      // the hand-off to the guide, so the wizard stops emitting after Finish
      // (`isCompletingRef`).
      onStepKeyChange={(key) => {
        void navigate({
          to: '/workspaces/$workspaceId/onboarding',
          params: { workspaceId },
          search: { step: key },
          replace: true,
        })
      }}
    />
  )
}
